import React, { useState } from "react";
import Icon from "../../../../components/Icon";
import { StatusBadge } from "../../../../components/Badge";
import ProviderIcon from "./ProviderIcon";
import { buildFullWebhookUrl } from "../../services/IntegrationsService";

export default function IntegratedProviderCard({
  provider,
  instances = [],
  isCatalog = false,
  onConnect,
  onEdit,
  onDelete,
  onTestConnection,
  connectionTestResults = {},
  onViewWebhookDocs,
}) {
  const [testingId, setTestingId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const name = provider.name || provider.id;
  const category = provider.category || "itsm";
  const description =
    provider.description || "Ingest webhook events and synchronize lifecycle updates.";
  const supportedEvents = provider.supported_events || [];

  const isConnected = !isCatalog && instances.length > 0;

  const handleCopy = async (instanceId, url) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = url;
        textarea.style.position = "fixed";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setCopiedId(instanceId);
      setTimeout(() => setCopiedId(null), 2500);
    } catch (err) {
      console.error("Failed to copy webhook url", err);
    }
  };

  const handleTest = async (instance) => {
    setTestingId(instance.id);
    try {
      await onTestConnection(instance);
    } finally {
      setTestingId(null);
    }
  };

  return (
    <div className={`unified-provider-card ${isConnected ? "is-connected" : "is-available"}`}>
      {/* CONNECTED STATE */}
      {isConnected ? (
        <div className="connected-card-content">
          {instances.map((instance, idx) => {
            const webhookPath =
              instance.webhook_url || `/webhooks/${instance.provider}/${instance.id}`;
            const fullUrl = buildFullWebhookUrl(webhookPath);
            const isCopied = copiedId === instance.id;
            const isTesting = testingId === instance.id;
            const testResult = connectionTestResults[instance.id];
            const currentError = testResult?.tested
              ? testResult.error
              : instance.last_error;

            return (
              <div
                key={instance.id}
                className={`instance-pipeline-card ${idx > 0 ? "instance-divider-top" : ""}`}
              >
                {/* Header */}
                <div className="card-top-header">
                  <div className="provider-info-left">
                    <div className="brand-icon-box">
                      <ProviderIcon provider={provider} size={22} />
                    </div>
                    <div>
                      <div className="title-status-line">
                        <h4 className="provider-name">{instance.name || name}</h4>
                        <StatusBadge status={instance.status || "active"} />
                      </div>
                      <div className="provider-meta-tags">
                        <span className="provider-sublabel">{name}</span>
                        <span className="dot-divider">•</span>
                        <span className={`provider-category-badge category-${category.toLowerCase()}`}>
                          {category.toUpperCase()}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Actions: Test, Edit, Delete */}
                  <div className="card-actions-right">
                    <button
                      type="button"
                      className="btn-icon-action"
                      onClick={() => handleTest(instance)}
                      disabled={isTesting}
                      title={isTesting ? "Testing connection..." : "Test connection credentials"}
                    >
                      <Icon name={isTesting ? "refresh" : "zap"} size={13} color={isTesting ? "#64748B" : "#2563EB"} />
                      <span className="btn-text-sm">{isTesting ? "Testing..." : "Test"}</span>
                    </button>

                    <button
                      type="button"
                      className="btn-icon-action"
                      onClick={() => onEdit(instance)}
                      title="Edit integration settings"
                    >
                      <Icon name="edit" size={13} color="#475569" />
                    </button>

                    <button
                      type="button"
                      className="btn-icon-action btn-delete"
                      onClick={() => onDelete(instance)}
                      title="Delete integration"
                    >
                      <Icon name="trash" size={13} color="#DC2626" />
                    </button>
                  </div>
                </div>

                {/* Webhook URL Endpoint Box with Setup Guide */}
                <div className="webhook-url-box">
                  <div className="webhook-url-header-row">
                    <div className="webhook-url-label">
                      <Icon name="link" size={12} color="#2563EB" />
                      <span>Incoming Webhook URL</span>
                    </div>
                    <button
                      type="button"
                      className="btn-guide-inline-link"
                      onClick={() => onViewWebhookDocs(provider, instance)}
                      title="View step-by-step webhook setup instructions"
                    >
                      <Icon name="info" size={12} color="#2563EB" />
                      <span>Setup Guide</span>
                    </button>
                  </div>
                  <div className="webhook-input-copy-group">
                    <input
                      type="text"
                      readOnly
                      value={fullUrl}
                      className="webhook-url-input"
                      spellCheck={false}
                      title={fullUrl}
                    />
                    <button
                      type="button"
                      className={`btn-copy-url ${isCopied ? "copied" : ""}`}
                      onClick={() => handleCopy(instance.id, fullUrl)}
                      title="Copy webhook URL to clipboard"
                    >
                      <Icon
                        name={isCopied ? "check" : "copy"}
                        size={12}
                        color={isCopied ? "#15803D" : "currentColor"}
                      />
                      <span>{isCopied ? "Copied!" : "Copy"}</span>
                    </button>
                  </div>
                </div>

                {/* Footer validation status & secondary info */}
                <div className="pipeline-footer-row">
                  <div className="footer-status-left">
                    {instance.last_validated_at ? (
                      <span className="timestamp-note">
                        <Icon name="clock" size={11} color="#64748B" />
                        Validated {new Date(instance.last_validated_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </span>
                    ) : (
                      <span className="timestamp-note">
                        <Icon name="checkCircle" size={11} color="#15803D" />
                        Pipeline Active
                      </span>
                    )}

                    {currentError && (
                      <span className="error-note" title={currentError}>
                        <Icon name="alert" size={11} color="#DC2626" />
                        {currentError}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* AVAILABLE / UNCONNECTED STATE */
        <div className="available-card-content">
          <div className="card-top-header">
            <div className="provider-info-left">
              <div className="brand-icon-box">
                <ProviderIcon provider={provider} size={22} />
              </div>
              <div>
                <div className="title-status-line">
                  <h4 className="provider-name">{name}</h4>
                  <span className={`provider-category-badge category-${category.toLowerCase()}`}>
                    {category.toUpperCase()}
                  </span>
                </div>
                <p className="available-desc-text">{description}</p>
              </div>
            </div>

            <span className={`connection-status-pill ${instances.length > 0 ? "connected" : "unconfigured"}`}>
              {instances.length > 0 ? (
                <>
                  <span className="live-dot" />
                  <span>{instances.length} Configured</span>
                </>
              ) : (
                "Ready to Connect"
              )}
            </span>
          </div>

          {supportedEvents.length > 0 && (
            <div className="events-pill-container">
              <div className="events-wrap">
                {supportedEvents.map((evt) => (
                  <span key={evt} className="event-pill">
                    {evt}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="card-footer-action-bar">
            <div className="footer-connect-row">
              <button
                type="button"
                className="btn-setup-guide-link"
                onClick={() => onViewWebhookDocs(provider, null)}
              >
                <Icon name="info" size={13} />
                <span>Setup Guide</span>
              </button>
              <button
                type="button"
                className="btn-connect-primary"
                onClick={() => onConnect(provider.id)}
              >
                <Icon name="plus" size={13} />
                <span>{instances.length > 0 ? `Add Another ${name}` : `Connect ${name}`}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
